import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "composerModal",
    "reviewModal",
    "sendingOverlay",
    "amountInput",
    "reviewAmount",
    "reviewCount",
    "reviewRoom",
    "confirmButton",
    "status",
    "successBox",
    "successText",
    "successLink"
  ]

  static values = {
    roomName: String,
    recipientCount: Number,
    submitUrl: String
  }

  connect() {
    this.pendingAirdrop = null
    this.hideAll()
  }

  openComposer(event) {
    event?.preventDefault()
    this.pendingAirdrop = null
    this.clearStatus()
    this.hideSuccess()
    if (this.hasComposerModalTarget) this.composerModalTarget.hidden = false
  }

  closeComposer() {
    if (this.hasComposerModalTarget) this.composerModalTarget.hidden = true
  }

  closeReview() {
    if (this.hasReviewModalTarget) this.reviewModalTarget.hidden = true
  }

  prepareReview() {
    const amount = this.normalizedAmount()
    if (!amount) {
      this.setStatus("Enter a valid MAZA amount.")
      return
    }

    this.pendingAirdrop = { amount }

    if (this.hasReviewAmountTarget) this.reviewAmountTarget.textContent = amount
    if (this.hasReviewCountTarget) this.reviewCountTarget.textContent = `${this.recipientCountValue || 0} recipients`
    if (this.hasReviewRoomTarget) this.reviewRoomTarget.textContent = this.roomNameValue || "Private room"

    this.clearStatus()
    this.closeComposer()
    if (this.hasReviewModalTarget) this.reviewModalTarget.hidden = false
  }

  async confirmSend(event) {
    event?.preventDefault()

    if (!this.pendingAirdrop?.amount) {
      this.setStatus("Airdrop draft missing.")
      return
    }

    if (!this.submitUrlValue) {
      this.setStatus("Room airdrop endpoint missing.")
      return
    }

    this.toggleConfirm(false)
    this.clearStatus()
    this.showSendingOverlay()

    try {
      const payload = await this.postJson(this.submitUrlValue, this.pendingAirdrop)
      this.showSuccess(payload)
      this.resetAmount()
      this.closeReview()
    } catch (error) {
      console.error("❌ Room MAZA airdrop failed:", error)
      this.setStatus(error.message || "Unable to broadcast the room airdrop right now.")
    } finally {
      this.hideSendingOverlay()
      this.toggleConfirm(true)
    }
  }

  normalizedAmount() {
    const raw = this.hasAmountInputTarget ? this.amountInputTarget.value.toString().trim() : ""
    if (!raw) return null

    const numeric = Number.parseFloat(raw)
    if (!Number.isFinite(numeric) || numeric <= 0) return null

    return numeric.toFixed(5).replace(/\.?0+$/, "")
  }

  resetAmount() {
    if (this.hasAmountInputTarget) this.amountInputTarget.value = ""
    this.pendingAirdrop = null
  }

  showSuccess(payload) {
    if (!this.hasSuccessBoxTarget) return

    if (this.hasSuccessTextTarget) {
      this.successTextTarget.textContent =
        payload.message || `Airdropped ${payload.amount_each} MAZA to ${payload.recipient_count} room members.`
    }

    if (this.hasSuccessLinkTarget) {
      const href = payload.tip_path || payload.explorer_url || "#"
      this.successLinkTarget.href = href
      this.successLinkTarget.hidden = href === "#"
    }

    this.successBoxTarget.hidden = false
  }

  hideSuccess() {
    if (this.hasSuccessBoxTarget) this.successBoxTarget.hidden = true
    if (this.hasSuccessLinkTarget) this.successLinkTarget.hidden = true
  }

  toggleConfirm(enabled) {
    if (!this.hasConfirmButtonTarget) return

    this.confirmButtonTarget.disabled = !enabled
    this.confirmButtonTarget.textContent = enabled ? "CONFIRM & AIRDROP" : "SENDING..."
  }

  hideAll() {
    if (this.hasComposerModalTarget) this.composerModalTarget.hidden = true
    if (this.hasReviewModalTarget) this.reviewModalTarget.hidden = true
    if (this.hasSendingOverlayTarget) this.sendingOverlayTarget.hidden = true
    this.hideSuccess()
    this.clearStatus()
  }

  clearStatus() {
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  showSendingOverlay() {
    if (this.hasSendingOverlayTarget) this.sendingOverlayTarget.hidden = false
  }

  hideSendingOverlay() {
    if (this.hasSendingOverlayTarget) this.sendingOverlayTarget.hidden = true
  }

  async postJson(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify(body)
    })

    let payload = {}
    try {
      payload = await response.json()
    } catch {
      payload = {}
    }

    if (!response.ok) {
      throw new Error(payload.error || `Request failed (${response.status})`)
    }

    return payload
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
